import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { registrationService } from '../services/svc_registration';
import { queryKeys } from '../lib/queryKeys';
import {
  UserInvitationCreate,
  UserInvitationResponse,
  RegistrationTokenInfo,
  UserRegistrationComplete,
  UserRegistrationCompleteResponse,
} from '../types/registrationTypes';

/**
 * TanStack Query hooks for user registration functionality
 * Following the established patterns in the codebase
 */

/**
 * Query hook for validating registration token
 * Used by the public registration page to check if a token is valid
 */
export const useTSQValidateToken = (token: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.registration.token(token),
    queryFn: () => registrationService.validateToken(token),
    enabled: enabled && !!token, // Only run if we have a token and it's enabled
    staleTime: 0, // Don't cache token validation - always check fresh
    gcTime: 0, // Don't keep in memory - tokens are one-time use
    retry: false, // Don't retry on failure - let the component handle it
    refetchOnWindowFocus: false, // Don't refetch when user returns to tab
    refetchOnReconnect: false, // Don't refetch on network reconnect
  });
};

/**
 * Mutation hook for sending user invitations (Admin only)
 * Used by the admin interface to invite new users
 */
export const useTSMInviteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (invitationData: UserInvitationCreate) => 
      registrationService.inviteUser(invitationData),
    onSuccess: (data: UserInvitationResponse) => {
      console.log('Invitation sent successfully:', data);
      // Could invalidate user list or other relevant queries if needed
      // queryClient.invalidateQueries({ queryKey: queryKeys.user.lists() });
    },
    onError: (error) => {
      console.error('Failed to send invitation:', error);
    },
  });
};

/**
 * Mutation hook for completing user registration
 * Used by the public registration page to finalize user account creation
 */
export const useTSMCompleteRegistration = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ token, registrationData }: { 
      token: string; 
      registrationData: UserRegistrationComplete;
    }) => registrationService.completeRegistration(token, registrationData),
    onSuccess: (data: UserRegistrationCompleteResponse) => {
      console.log('Registration completed successfully:', data);
      // Clear the token validation cache since it's been used
      queryClient.removeQueries({ queryKey: queryKeys.registration.all });
      // Could also clear auth cache to ensure fresh state
      // queryClient.invalidateQueries({ queryKey: queryKeys.auth.status() });
    },
    onError: (error) => {
      console.error('Failed to complete registration:', error);
    },
  });
};